import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  styled,
  CircularProgress,
} from '@mui/material';
import validator from 'validator';
import axios from 'axios';
import Logo from '../../../Assets/BugLogo.png';

const FormContainer = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  width: '100%',
  margin: '40px 20px',
  fontFamily: 'Arial, sans-serif',
  color: '#333',
  lineHeight: 1.6,
  border: '1px solid #ccc',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: '#f9f9f9',
  padding: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    margin: '20px 10px',
  },
}));

const OptIn = () => {
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [textAgree, setTextAgree] = useState(false);

  const [companyNameError, setCompanyNameError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');

  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (emailToValidate) => {
    return validator.isEmail(String(emailToValidate));
  };

  const validateMobile = (mobile) => {
    return validator.isMobilePhone(String(mobile), 'en-US');
  };

  useEffect(() => {
    const isCompanyNameValid = companyName.trim() !== '';
    const isFirstNameValid = firstName.trim() !== '';
    const isLastNameValid = lastName.trim() !== '';
    const isMobileValid = mobileNumber.trim() !== '' && validateMobile(mobileNumber.trim());
    const isEmailFieldValid = email.trim() === '' || validateEmail(email.trim());

    setIsFormValid(
      isCompanyNameValid &&
        isFirstNameValid &&
        isLastNameValid &&
        isMobileValid &&
        isEmailFieldValid
    );
  }, [companyName, firstName, lastName, mobileNumber, email]);

  const handleCompanyNameChange = (e) => {
    setCompanyName(e.target.value);
    setCompanyNameError('');
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
    setFirstNameError('');
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
    setLastNameError('');
  };

  const handleMobileChange = (e) => {
    setMobileNumber(e.target.value);
    setMobileError('');
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (email.trim() === '' || validateEmail(e.target.value)) {
      setEmailError('');
    }
  };

  const handleCompanyNameBlur = () => {
    if (companyName.trim() === '') setCompanyNameError('Company Name cannot be empty');
    else setCompanyNameError('');
  };

  const handleFirstNameBlur = () => {
    if (firstName.trim() === '') setFirstNameError('First Name cannot be empty');
    else setFirstNameError('');
  };

  const handleLastNameBlur = () => {
    if (lastName.trim() === '') setLastNameError('Last Name cannot be empty');
    else setLastNameError('');
  };

  const handleMobileBlur = () => {
    const trimmedMobile = mobileNumber.trim();
    if (trimmedMobile === '') setMobileError('Mobile Number cannot be empty');
    else if (!validateMobile(trimmedMobile)) setMobileError('Please enter a valid US phone number');
    else setMobileError('');
  };

  const handleEmailBlur = () => {
    const trimmedEmail = email.trim();
    if (trimmedEmail === '') {
      setEmailError('');
    } else if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    handleCompanyNameBlur();
    handleFirstNameBlur();
    handleLastNameBlur();
    handleMobileBlur();
    handleEmailBlur();

    const isEmailOkayForSubmission =
      email.trim() === '' || (!emailError && validateEmail(email.trim()));

    const canSubmit =
      companyName.trim() !== '' &&
      !companyNameError &&
      firstName.trim() !== '' &&
      !firstNameError &&
      lastName.trim() !== '' &&
      !lastNameError &&
      mobileNumber.trim() !== '' &&
      validateMobile(mobileNumber.trim()) &&
      !mobileError &&
      isEmailOkayForSubmission;

    if (canSubmit) {
      setIsLoading(true);
      setSubmissionError(null);

      try {
        const payload = {
          companyName: companyName.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          mobileNumber: mobileNumber.trim(),
          emailAddress: email.trim() ? email.trim() : null,
        };

        await axios.post('/users/optIn', payload);
        setIsSubmitted(true);
      } catch (error) {
        // console.error('Submission failed:', error);
        setSubmissionError('Opt-in unsuccessful, please try again');
        setIsLoading(false);
      }
    } else {
      // console.log('Submit blocked due to errors or incomplete fields.');
    }
  };

  if (isSubmitted) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          textAlign: 'center',
        }}
      >
        <FormContainer>
          <Typography variant='h5' sx={{ py: 4 }}>
            You have opted in successfully!
          </Typography>
        </FormContainer>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: '20px', md: '40px' },
          mx: { xs: '20px', md: '40px' },
        }}
      >
        <img src={Logo} alt='Logo' style={{ maxWidth: '300px', height: 'auto' }} />
      </Box>

      <FormContainer>
        <Typography variant='h4' sx={{ pt: 3, pb: 1, fontWeight: 'bold', textAlign: 'center' }}>
          SMS Opt-In
        </Typography>
        <Typography variant='subtitle1' sx={{ pb: 4, textAlign: 'center' }}>
          Please fill out the following form to opt in to receiving SMS messages.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label='Company Name'
              variant='outlined'
              fullWidth
              value={companyName}
              onChange={handleCompanyNameChange}
              onBlur={handleCompanyNameBlur}
              required
              error={!!companyNameError}
              helperText={companyNameError}
              disabled={isLoading}
            />
            {/* First Name and Last Name in a row */}
            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 } }}>
              <TextField
                label='First Name'
                variant='outlined'
                fullWidth
                value={firstName}
                onChange={handleFirstNameChange}
                onBlur={handleFirstNameBlur}
                required
                error={!!firstNameError}
                helperText={firstNameError}
                disabled={isLoading}
              />
              <TextField
                label='Last Name'
                variant='outlined'
                fullWidth
                value={lastName}
                onChange={handleLastNameChange}
                onBlur={handleLastNameBlur}
                required
                error={!!lastNameError}
                helperText={lastNameError}
                disabled={isLoading}
              />
            </Box>
            <TextField
              label='Mobile Number'
              variant='outlined'
              fullWidth
              type='tel'
              value={mobileNumber}
              onChange={handleMobileChange}
              onBlur={handleMobileBlur}
              required
              error={!!mobileError}
              helperText={mobileError}
              disabled={isLoading}
            />
            <TextField
              label='Email Address (Optional)'
              variant='outlined'
              fullWidth
              type='text'
              inputMode='email'
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              error={!!emailError}
              helperText={emailError}
              disabled={isLoading}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={textAgree}
                  onChange={(e) => setTextAgree(e.target.checked)}
                  name='textAgree'
                  sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                />
              }
              label={
                <Typography variant='subtitle2' align='justify' sx={{ color: '#333' }}>
                  By checking this box, you agree to receive recurring automated promotional and
                  personalized marketing text messages (e.g. cart reminders) from Sports South at
                  the cell number listed on this form. Message frequency varies. Consent is not a
                  condition of any purchase. Reply HELP for help and STOP to cancel. Msg frequency
                  varies. Msg & data rates may apply. View Terms & Privacy. Firearms that are not
                  legally available in all 50 states will not be advertised via SMS.
                </Typography>
              }
              sx={{ mt: 1 }}
            />

            {submissionError && (
              <Typography color='error' sx={{ textAlign: 'center', mt: -1, mb: -2 }}>
                {submissionError}
              </Typography>
            )}

            <Button
              type='submit'
              variant='contained'
              color='primary'
              size='large'
              sx={{ mt: 1, py: 1.5 }}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? <CircularProgress size={24} color='inherit' /> : 'Submit'}
            </Button>
          </Box>
        </form>
      </FormContainer>
    </Box>
  );
};

export default OptIn;
